import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import AdmittedPatientsModal from "../../components/ipd/admission/AdmittedPatientsModal";
import CommonButton from "../../components/common11/Buttons/CommonButton";
import CommonModal from "../../components/common11/commonModals/CommonModal";
import CommonDatePicker from "../../components/common11/formInputs/CommonDatePicker";
import CommonTimePicker from "../../components/common11/formInputs/CommonTimePicker";
import SearchDropdown from "../../components/common11/formInputs/SearchDropdown";
import InputField from "../../components/common11/formInputs/InputField";
import InputAreaField from "../../components/common11/formInputs/InputAreaField";
import CommonCheckbox from "../../components/common11/formInputs/CommonCheckbox";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../components/common11/toastUtils";
import {
  formatRoundTime,
  listAdmittedPatientsForDoctorRound,
  mapWebPatientToAdmissionRow,
  toApiTime,
} from "../../services/doctorRoundService";
import {
  addAdvisedService,
  fetchDoctorRoundById,
  fetchDoctorRounds,
  resetForm,
  saveDoctorRoundNote,
  searchDoctorRoundServices,
  setPatient,
  upsertDoctor,
} from "../../store/slices/drRoundSlice";
import { rtfToPlainText } from "../../utils/rtfToPlainText";

const mapAdmissionRowToPatient = (row) => ({
  uhid: row.uhid || "",
  patientName: row.patientName || "",
  contact: row.contact || "",
  patientGender: row.gender || "",
  patientAge: row.age || "",
  doctorName: row.doctorName || "",
  doctorId: row.doctorId != null ? Number(row.doctorId) : null,
  wardName: row.wardName || "",
  bedNo: row.bedNo || "",
  admissionId: Number(row.admissionId) || 0,
  regId: row.regId != null ? Number(row.regId) : null,
});

const parseCount = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const sameId = (a, b) => {
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
};

const toDoctorOption = (doctorId, doctorName) => {
  const id = Number(doctorId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    label: doctorName || `Doctor #${id}`,
    value: id,
  };
};

const DrRound = () => {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const {
    patient,
    doctors,
    services,
    servicesLoading,
    advisedServices,
    roundHistory,
    editingDoctorNoteId,
    loading,
    saving,
  } = useSelector((state) => state.drRound);

  const now = new Date();
  const initialTime = `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`;

  const { control, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      patientSearch: null,
      date: new Date().toISOString().split("T")[0],
      time: initialTime,
      doctor: null,
      addedBy: authUser?.userName || "",
      tpr: false,
      tprTimes: "",
      intakeFlag: false,
      intakeTimes: "",
      doctorNotes: "",
      adviseService: null,
    },
  });

  const [admittedModalOpen, setAdmittedModalOpen] = useState(false);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listPage, setListPage] = useState(0);
  const [listRowsPerPage, setListRowsPerPage] = useState(10);
  const [admissionRows, setAdmissionRows] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const doctorOptions = useMemo(() => {
    const options = doctors.map((d) => ({
      id: Number(d.doctorId),
      label: d.doctorName,
      value: Number(d.doctorId),
    }));
    // Ensure selected patient's doctor appears even if not in mock list yet
    if (
      patient?.doctorId &&
      !options.some((o) => sameId(o.value, patient.doctorId))
    ) {
      const injected = toDoctorOption(patient.doctorId, patient.doctorName);
      if (injected) options.push(injected);
    }
    return options;
  }, [doctors, patient?.doctorId, patient?.doctorName]);

  const serviceOptions = useMemo(
    () =>
      services.map((service) => ({
        id: Number(service.serviceId),
        label: service.serviceName,
        value: Number(service.serviceId),
        serviceId: Number(service.serviceId),
        serviceCode: service.serviceCode || "",
      })),
    [services]
  );

  const formBusy = loading || saving;

  const handleServiceSearch = useCallback(
    (term) => {
      const q = (term || "").trim();
      if (!q) return Promise.resolve();
      return dispatch(searchDoctorRoundServices(q));
    },
    [dispatch]
  );

  const loadIpPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const data = await listAdmittedPatientsForDoctorRound({
        isDischarged: false,
      });
      setAdmissionRows((data || []).map(mapWebPatientToAdmissionRow));
    } catch (error) {
      setAdmissionRows([]);
      showErrorToast(error.message || "Failed to load admitted patients");
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIpPatients();
  }, [loadIpPatients]);

  useEffect(() => {
    if (authUser?.userName) {
      setValue("addedBy", authUser.userName);
    }
  }, [authUser?.userName, setValue]);

  const patientSearchOptions = useMemo(
    () =>
      admissionRows
        .filter((row) => row.status === "Admitted")
        .map((row) => ({
          id: row.admissionId,
          label: [
            row.patientName,
            row.uhid,
            row.age ? `${row.age}Y` : null,
            row.doctorName,
          ]
            .filter(Boolean)
            .join(" | "),
          value: row.patientName,
          row,
        })),
    [admissionRows]
  );

  const loadRoundsForPatient = useCallback(
    async (admissionId) => {
      if (!admissionId) return false;
      const result = await dispatch(fetchDoctorRounds(admissionId));
      if (fetchDoctorRounds.rejected.match(result)) {
        showErrorToast(result.payload || "Failed to load doctor rounds");
        return false;
      }
      return true;
    },
    [dispatch]
  );

  const handleOpenDoctorRoundList = async () => {
    if (!patient?.admissionId) {
      showWarningToast("Please select a patient first");
      return;
    }
    setListPage(0);
    setListModalOpen(true);
    await loadRoundsForPatient(patient.admissionId);
  };

  const listTotalPages = Math.max(
    1,
    Math.ceil(roundHistory.length / listRowsPerPage)
  );
  const pagedRoundHistory = useMemo(() => {
    const start = listPage * listRowsPerPage;
    return roundHistory.slice(start, start + listRowsPerPage);
  }, [roundHistory, listPage, listRowsPerPage]);

  const listRangeStart =
    roundHistory.length === 0 ? 0 : listPage * listRowsPerPage + 1;
  const listRangeEnd = Math.min(
    (listPage + 1) * listRowsPerPage,
    roundHistory.length
  );

  const handleSelectPatient = (row) => {
    if (!row) {
      dispatch(setPatient(null));
      return;
    }
    const mapped = mapAdmissionRowToPatient(row);
    dispatch(setPatient(mapped));
    if (mapped.doctorId && mapped.doctorName) {
      dispatch(
        upsertDoctor({
          doctorId: mapped.doctorId,
          doctorName: mapped.doctorName,
        })
      );
    }
    setValue("patientSearch", {
      id: mapped.admissionId || mapped.uhid,
      label: mapped.patientName || "",
      value: mapped.contact || mapped.uhid || "",
    });

    if (mapped.doctorId) {
      const matchedDoctor =
        doctorOptions.find((d) => sameId(d.value, mapped.doctorId)) ||
        toDoctorOption(mapped.doctorId, mapped.doctorName);
      if (matchedDoctor) {
        setValue("doctor", matchedDoctor);
      }
    }
  };

  const handlePatientSearchSelect = (selectedOption) => {
    if (!selectedOption) {
      handleSelectPatient(null);
      setValue("patientSearch", null);
      return;
    }
    if (selectedOption.row) {
      handleSelectPatient(selectedOption.row);
    }
  };

  const handleAddAdvisedService = () => {
    if (formBusy) return;
    const selectedService = watch("adviseService");
    if (!selectedService) {
      showWarningToast("Please select a service");
      return;
    }
    const serviceId = Number(selectedService.serviceId ?? selectedService.id);
    if (!Number.isFinite(serviceId) || serviceId <= 0) {
      showWarningToast("Invalid service selected");
      return;
    }
    if (advisedServices.some((item) => sameId(item.serviceId, serviceId))) {
      showWarningToast("Service already added");
      return;
    }
    dispatch(
      addAdvisedService({
        id: `${Date.now()}-${serviceId}`,
        serviceId,
        serviceCode: selectedService.serviceCode || serviceId,
        serviceName: selectedService.label || "",
      })
    );
    setValue("adviseService", null);
  };

  const handleResetForm = () => {
    const today = new Date().toISOString().split("T")[0];
    const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
    reset({
      patientSearch: patient
        ? {
            id: patient.admissionId || patient.uhid,
            label: patient.patientName,
            value: patient.contact || patient.uhid,
          }
        : null,
      date: today,
      time: timeStr,
      doctor: null,
      addedBy: authUser?.userName || "",
      tpr: false,
      tprTimes: "",
      intakeFlag: false,
      intakeTimes: "",
      doctorNotes: "",
      adviseService: null,
    });
    dispatch(resetForm());
  };

  const handleEditRoundRow = async (row) => {
    if (!row?.doctorNoteId || formBusy) return;

    const result = await dispatch(fetchDoctorRoundById(row.doctorNoteId));
    if (fetchDoctorRoundById.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load doctor round note");
      return;
    }

    const note = result.payload?.note || row;
    if (note.doctorId && note.doctorName) {
      dispatch(
        upsertDoctor({
          doctorId: note.doctorId,
          doctorName: note.doctorName,
        })
      );
    }
    const matchedDoctor =
      toDoctorOption(note.doctorId, note.doctorName) ||
      doctorOptions.find((d) => sameId(d.value, note.doctorId));

    setValue("date", note.date || new Date().toISOString().split("T")[0]);
    setValue("time", formatRoundTime(note.time) || initialTime);
    setValue("doctor", matchedDoctor || null);
    setValue("doctorNotes", rtfToPlainText(note.doctorNotes || ""));
    setValue("addedBy", note.addedByName || authUser?.userName || "");
    setValue("tpr", Boolean(note.isTpr));
    setValue(
      "tprTimes",
      note.tprCount != null && note.tprCount !== 0 ? String(note.tprCount) : ""
    );
    setValue("intakeFlag", Boolean(note.isIntake));
    setValue(
      "intakeTimes",
      note.intakeCount != null && note.intakeCount !== 0
        ? String(note.intakeCount)
        : ""
    );
    setListModalOpen(false);
    showSuccessToast("Round note loaded for edit");
  };

  const handleSave = handleSubmit(async (data) => {
    if (formBusy) return;
    if (!patient?.patientName) {
      showWarningToast("Please select a patient first");
      return;
    }
    if (!patient.admissionId || !patient.regId) {
      showWarningToast("Selected patient is missing admission or registration id");
      return;
    }
    if (!data.doctor?.value && !data.doctor?.id) {
      showWarningToast("Please select a doctor");
      return;
    }
    // Auth temporarily skipped — fall back to userId 1 for SP addedBy/updatedBy
    const userId = Number(authUser?.userId || 1);
    const advice = advisedServices.map((s) => s.serviceName).filter(Boolean).join(", ");
    const serviceIds = advisedServices
      .map((s) => Number(s.serviceId))
      .filter((id) => Number.isFinite(id) && id > 0)
      .join(",");

    const createPayload = {
      regId: Number(patient.regId),
      admissionId: Number(patient.admissionId),
      date: data.date,
      time: toApiTime(data.time),
      doctorId: Number(data.doctor.value ?? data.doctor.id),
      doctorNotes: data.doctorNotes || "",
      addedBy: userId,
      isTpr: Boolean(data.tpr),
      tprCount: parseCount(data.tprTimes) ?? 0,
      tprSlot: "H",
      // Default 1-hour interval when TPR is enabled (form has count only)
      tprTime: Boolean(data.tpr) && (parseCount(data.tprTimes) ?? 0) > 0 ? 1 : 0,
      isIntake: Boolean(data.intakeFlag),
      intakeCount: parseCount(data.intakeTimes) ?? 0,
      intakeSlot: "H",
      intakeTime:
        Boolean(data.intakeFlag) && (parseCount(data.intakeTimes) ?? 0) > 0 ? 1 : 0,
      advice: advice || "",
      prescriptionId: null,
      serviceIds: serviceIds || null,
    };

    // Create omits updatedBy; update includes rebuildSchedule for web SP
    const updatePayload = {
      date: createPayload.date,
      time: createPayload.time,
      doctorId: createPayload.doctorId,
      doctorNotes: createPayload.doctorNotes,
      updatedBy: userId,
      isTpr: createPayload.isTpr,
      tprCount: createPayload.tprCount,
      tprSlot: createPayload.tprSlot,
      tprTime: createPayload.tprTime,
      isIntake: createPayload.isIntake,
      intakeCount: createPayload.intakeCount,
      intakeSlot: createPayload.intakeSlot,
      intakeTime: createPayload.intakeTime,
      advice: createPayload.advice,
      serviceIds: createPayload.serviceIds,
      rebuildSchedule: false,
    };

    const result = await dispatch(
      saveDoctorRoundNote({
        doctorNoteId: editingDoctorNoteId,
        payload: editingDoctorNoteId ? updatePayload : createPayload,
      })
    );

    if (saveDoctorRoundNote.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to save doctor round");
      return;
    }

    showSuccessToast(
      editingDoctorNoteId
        ? "Doctor round note updated successfully"
        : "Doctor round note saved successfully"
    );
    handleResetForm();
    await loadRoundsForPatient(patient.admissionId);
  });

  const patientName = patient?.patientName || "—";
  const contact = patient?.contact || "—";
  const uhid = patient?.uhid || "—";
  const genderAge = patient?.patientAge
    ? [patient?.patientGender, `${patient.patientAge}Y`].filter(Boolean).join(" | ") ||
      `${patient.patientAge}Y`
    : "—";
  const doctorName = patient?.doctorName || "—";
  const currentBed =
    [patient?.wardName, patient?.bedNo].filter(Boolean).join(" - ") || "—";

  const patientInfoHeader = patient ? (
    <div className="bg-gray-100 rounded-lg p-3 border border-gray-200 shadow-sm">
      <div className="flex items-center gap-6">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
            <span className="text-lg font-semibold text-gray-600">
              {patientName !== "—" ? patientName.charAt(0).toUpperCase() : "?"}
            </span>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-3 text-sm">
          <div className="flex items-baseline gap-2 col-span-1">
            <span className="text-[#217E90] font-semibold">{patientName}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">UHID</span>
            <span className="text-[#217E90] font-semibold">: {uhid}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Gender / Age</span>
            <span className="text-[#217E90] font-semibold">: {genderAge}</span>
          </div>
          <div className="flex items-baseline gap-2 col-span-1">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Contact</span>
            <span className="text-[#217E90] font-semibold">: {contact}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Doctor name</span>
            <span className="text-[#217E90] font-semibold">: {doctorName}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Current bed</span>
            <span className="text-[#217E90] font-semibold">: {currentBed}</span>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
      {patientsLoading
        ? "Loading admitted patients..."
        : "Search and select a patient to begin the doctor round entry."}
    </div>
  );

  const doctorRoundListSection = (
    <div className="rounded border border-gray-300">
      <div className="overflow-auto max-h-[55vh]">
        <table className="min-w-full text-[11px]">
          <thead className="bg-teal-700 text-white sticky top-0">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">Date</th>
              <th className="px-2 py-1.5 text-left font-semibold">Time</th>
              <th className="px-2 py-1.5 text-left font-semibold">Doctor Name</th>
              <th className="px-2 py-1.5 text-left font-semibold">Added By Name</th>
              <th className="px-2 py-1.5 text-left font-semibold">Updated By Name</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  Loading doctor round records...
                </td>
              </tr>
            ) : roundHistory.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  No doctor round records.
                </td>
              </tr>
            ) : (
              pagedRoundHistory.map((row) => (
                <tr
                  key={row.doctorNoteId || `${row.date}-${row.time}`}
                  className="border-t border-gray-200 cursor-pointer hover:bg-[#E9F5FE]"
                  onClick={() => handleEditRoundRow(row)}
                  title="Click to edit"
                >
                  <td className="px-2 py-1.5 text-gray-700">{row.date || "—"}</td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {formatRoundTime(row.time) || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {row.doctorName || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {row.addedByName || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {row.updatedByName || row.userName || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-2 py-2 text-[11px] text-gray-600">
        <div className="flex items-center gap-2">
          <span>Rows per page</span>
          <select
            className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px]"
            value={listRowsPerPage}
            onChange={(e) => {
              setListRowsPerPage(Number(e.target.value));
              setListPage(0);
            }}
          >
            {[5, 10, 25, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span>
            {listRangeStart}-{listRangeEnd} of {roundHistory.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-2 py-0.5 disabled:opacity-40"
            disabled={listPage <= 0 || loading}
            onClick={() => setListPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <span className="px-1">
            {Math.min(listPage + 1, listTotalPages)} / {listTotalPages}
          </span>
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-2 py-0.5 disabled:opacity-40"
            disabled={listPage >= listTotalPages - 1 || loading || roundHistory.length === 0}
            onClick={() =>
              setListPage((p) => Math.min(listTotalPages - 1, p + 1))
            }
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  const doctorRoundFormSection = (
    <div className="rounded border border-gray-200 p-3 space-y-3">
      {editingDoctorNoteId ? (
        <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Editing note #{editingDoctorNoteId}. Save to update, or Reset to create a new note.
        </p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <CommonDatePicker
          name="date"
          control={control}
          label="Date"
          type="date"
          fontSize="text-xs"
          isFloatingLabel
        />
        <CommonTimePicker
          name="time"
          control={control}
          label="Time"
          fontSize="text-xs"
          isFloatingLabel
          use24Hour={false}
          format="hh:mm A"
        />
        <SearchDropdown
          name="doctor"
          control={control}
          label="Doctor"
          dataArray={doctorOptions}
          placeholder="Select"
          isSearchable
          isClearable
          fontSize="text-xs"
          isFloatingLabel
          disabled={formBusy}
        />
        <InputField
          name="addedBy"
          control={control}
          label="Added By"
          fontSize="text-xs"
          isFloatingLabel
          disabled
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs items-center">
        <div className="flex items-center gap-2">
          <CommonCheckbox
            name="tpr"
            control={control}
            label="TPR"
            fontSize="text-xs"
            checkboxSize="h-4 w-4"
          />
          <span className="text-gray-600">Times</span>
          <InputField
            name="tprTimes"
            control={control}
            label=""
            type="number"
            fontSize="text-xs"
            isFloatingLabel={false}
            className="w-20"
          />
        </div>
        <div className="flex items-center gap-2">
          <CommonCheckbox
            name="intakeFlag"
            control={control}
            label="Intake"
            fontSize="text-xs"
            checkboxSize="h-4 w-4"
          />
          <span className="text-gray-600">Times</span>
          <InputField
            name="intakeTimes"
            control={control}
            label=""
            type="number"
            fontSize="text-xs"
            isFloatingLabel={false}
            className="w-20"
          />
        </div>
      </div>

      <InputAreaField
        name="doctorNotes"
        control={control}
        label="Doctor Notes"
        rows={10}
        fontSize="text-xs"
        inputBgColor="bg-white"
        enableSpeechToText
      />

      <div className="space-y-2">
        <p className="text-xs font-semibold text-[#217E90]">Advise Services</p>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-10">
            <SearchDropdown
              name="adviseService"
              control={control}
              label="Search Service"
              dataArray={serviceOptions}
              placeholder="Search service"
              isSearchable
              isClearable
              fontSize="text-xs"
              isFloatingLabel
              onSearch={handleServiceSearch}
              isLoading={servicesLoading}
              disabled={formBusy}
            />
          </div>
          <div className="sm:col-span-2">
            <CommonButton
              label="Add"
              type="button"
              size="sm"
              className="w-full bg-[#217E90] text-white hover:bg-[#1a5e70] text-xs sm:text-sm"
              onClick={handleAddAdvisedService}
              disabled={formBusy}
            />
          </div>
        </div>
        <div className="rounded border border-gray-300">
          <div className="overflow-auto">
            <table className="min-w-full text-[11px]">
              <thead className="bg-teal-700 text-white">
                <tr>
                  <th className="px-2 py-1 text-left font-semibold">Code</th>
                  <th className="px-2 py-1 text-left font-semibold">Service Name</th>
                </tr>
              </thead>
              <tbody>
                {advisedServices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-2 py-3 text-center text-gray-500 border-t border-gray-200"
                    >
                      No services added.
                    </td>
                  </tr>
                ) : (
                  advisedServices.map((row) => (
                    <tr key={row.id} className="border-t border-gray-200">
                      <td className="px-2 py-1.5 text-gray-700">{row.serviceCode}</td>
                      <td className="px-2 py-1.5 text-gray-700">{row.serviceName}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-2 sm:p-3 pb-24">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="sticky top-0 z-10 rounded-t-lg px-3 py-2 border-b border-gray-200 bg-white shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-[#217E90]">Doctor Round</h2>
        </div>

        <div className="p-3 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <div className="w-full sm:flex-1 sm:max-w-2xl">
              <SearchDropdown
                name="patientSearch"
                control={control}
                dataArray={patientSearchOptions}
                onChange={handlePatientSearchSelect}
                label="Search by patient name or mobile number"
                isSearchable
                isClearable
                fontSize="text-xs sm:text-sm"
                isFloatingLabel
              />
            </div>
            <div className="flex-shrink-0">
              <CommonButton
                label="Admitted Patients"
                type="button"
                size="sm"
                className="bg-[#217E90] text-white hover:bg-[#1a5e70] px-3 py-1.5 text-xs sm:text-sm w-full sm:w-auto"
                onClick={() => setAdmittedModalOpen(true)}
              />
            </div>
          </div>

          {patientInfoHeader}
          {patient && doctorRoundFormSection}
        </div>
      </div>

      <AdmittedPatientsModal
        isOpen={admittedModalOpen}
        onClose={() => setAdmittedModalOpen(false)}
        onSelectPatient={handleSelectPatient}
        onRowDoubleClick={handleSelectPatient}
        rows={admissionRows}
      />

      <CommonModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        title="Doctor Round List"
        size="lg"
        maxHeightVh={90}
        paperSx={{
          width: { xs: "calc(100vw - 16px)", lg: "min(90vw, 960px)" },
          maxWidth: { lg: "min(90vw, 960px)" },
        }}
        disableBackdropClose={false}
        showCloseButton
        actions={[]}
      >
        {doctorRoundListSection}
      </CommonModal>

      <div className="fixed bottom-4 right-4 z-50 flex flex-wrap justify-end gap-2 sm:gap-3 pr-2 sm:pr-6">
        <CommonButton
          label={loading && listModalOpen ? "Loading..." : "Doctor Round List"}
          onClick={handleOpenDoctorRoundList}
          variant="reset-outlined"
          size="sm"
          type="button"
          disabled={formBusy}
          className="px-3 md:px-5 text-xs md:text-sm rounded-full bg-emerald-50 text-emerald-700 border border-emerald-400 hover:bg-emerald-100"
        />
        <CommonButton
          label="Reset"
          onClick={handleResetForm}
          variant="danger-outline"
          size="sm"
          type="button"
          disabled={formBusy}
          className="px-3 md:px-5 text-xs md:text-sm"
        />
        <CommonButton
          label={
            saving
              ? "Saving..."
              : loading
                ? "Loading..."
                : editingDoctorNoteId
                  ? "Update"
                  : "Save"
          }
          onClick={handleSave}
          variant="primary-filled"
          size="sm"
          type="button"
          disabled={formBusy || !patient}
        />
      </div>
    </div>
  );
};

export default DrRound;
